import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Heart } from 'lucide-react'
import { clsx } from 'clsx'
import { authApiClient } from '~/lib/api-client'
import { Button } from '@heroui/react'
import { useTranslations } from 'use-intl'
import { toast } from 'sonner'
import { useAuthModalStore } from '~/stores/use-auth-modal-store'
import { useFavorites } from '../hooks/use-favorites'
import { createSessionQuery } from '~/lib/session-query'

interface FavoriteButtonProps {
  shopId: number
  className?: string
  showText?: boolean
}

export function FavoriteButton({ shopId, className, showText = false }: Readonly<FavoriteButtonProps>) {
  const queryClient = useQueryClient()
  const { data: session } = useQuery(createSessionQuery())
  const { data: favorites, isLoading } = useFavorites()
  const t = useTranslations('discovery')

  const isFavorite = favorites?.some((f: { shopId: number }) => f.shopId === shopId) ?? false

  // Mutation to toggle favorite status
  const toggleMutation = useMutation({
    mutationFn: async () => {
      if (isFavorite) {
        const res = await (authApiClient as any).users.me.favorites[':shopId'].$delete({
          param: { shopId: String(shopId) },
        })
        if (!res.ok && res.status !== 404) {
          if (res.status === 401) throw new Error('Unauthorized')
          throw new Error('Failed to remove favorite')
        }
        return { isFavorite: false, shopId }
      } else {
        const res = await (authApiClient as any).users.me.favorites.$post({
          json: { shopId },
        })
        if (!res.ok && res.status !== 409) {
          if (res.status === 401) throw new Error('Unauthorized')
          throw new Error('Failed to add favorite')
        }
        return { isFavorite: true, shopId }
      }
    },
    onMutate: async () => {
      // Optimistically update the UI list
      await queryClient.cancelQueries({ queryKey: ['favorites', 'list'] })
      const previousFavorites = queryClient.getQueryData<{ shopId: number }[]>(['favorites', 'list']) || []
      
      queryClient.setQueryData(['favorites', 'list'], (old: { shopId: number }[] = []) => {
        if (isFavorite) {
          return old.filter(f => f.shopId !== shopId)
        } else {
          return [...old, { shopId }]
        }
      })
      return { previousFavorites }
    },
    onError: (err, newTodo, context) => {
      if (context?.previousFavorites) {
        queryClient.setQueryData(['favorites', 'list'], context.previousFavorites)
      }
      toast.error(t('favoriteError'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'list'] })
      // Invalidate nearby shops to refresh the list if we are filtering by favorites
      queryClient.invalidateQueries({ queryKey: ['nearby-shops'] })
    },
    onSuccess: (data) => {
      if (data.isFavorite) {
        toast.success(t('favoriteAdded'))
      } else {
        toast.success(t('favoriteRemoved'))
      }
    }
  })

  const openAuthModal = useAuthModalStore(s => s.openAuthModal)

  const handlePress = () => {
    // Check session directly — the authoritative source of auth state.
    // Using `favorites === undefined` was unreliable because SSR hydration
    // can inject a stale session into React Query cache.
    if (!session?.user) {
      openAuthModal()
      return
    }
    toggleMutation.mutate()
  }

  return (
    <Button
      isIconOnly={!showText}
      variant={isFavorite ? 'danger-soft' : 'ghost'}

      className={clsx(
        "rounded-full h-10 transition-transform active:scale-95",
        showText ? "px-4" : "min-w-10 w-10",
        isFavorite ? "bg-red-50 text-red-500 dark:bg-red-950/30" : "text-stone-500 hover:text-stone-900 dark:hover:text-stone-100",
        className
      )}
      onPress={handlePress}
      onClick={(e) => {
        // Prevent event bubbling to the parent ShopCard which might trigger navigation
        e.preventDefault()
        e.stopPropagation()
      }}
      isDisabled={isLoading}
      aria-label={isFavorite ? t('removeFavorite', { defaultValue: 'Remove from favorites' }) : t('addFavorite', { defaultValue: 'Add to favorites' })}
      data-testid="favorite-button"
    >
      <Heart 
        className={clsx(
          "w-5 h-5 transition-all duration-300", 
          isFavorite && "fill-current scale-110"
        )} 
      />
      {showText && <span className="ml-2 font-medium">{isFavorite ? t('saved', { defaultValue: 'Saved' }) : t('save', { defaultValue: 'Save' })}</span>}
    </Button>
  )
}
